import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader, useWorkspace } from '../../components/layout/WorkspaceShell';
import Btn from '../../components/ui/Btn';
import { Card, Input, Spinner, Badge } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import { ErrorBanner } from '../auth/SignInScreen';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../lib/toast';
import { useIsMobile } from '../../lib/useBreakpoint';

const PRESET_COLORS = ['#0a0f2e', '#3dc75b', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899', '#14b8a6', '#ef4444'];

function toSlug(str) {
  return (str || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 40);
}

export default function SettingsPanel() {
  const { teamId, team, isOwner, refresh } = useWorkspace();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const toast = useToast();

  // Form state
  const [name, setName]       = useState(team?.name || '');
  const [slug, setSlug]       = useState(team?.slug || '');
  const [color, setColor]     = useState(team?.primary_color || '#0a0f2e');
  const [language, setLanguage] = useState(team?.language_default || 'en');
  const [smsEnabled, setSmsEnabled] = useState(!!team?.sms_enabled);

  const [logoFile, setLogoFile]   = useState(null);
  const [logoPreview, setLogoPreview] = useState(team?.logo_url || null);

  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(null);

  // Reset form when team loads/changes
  useEffect(() => {
    if (team) {
      setName(team.name || '');
      setSlug(team.slug || '');
      setColor(team.primary_color || '#0a0f2e');
      setLanguage(team.language_default || 'en');
      setSmsEnabled(!!team.sms_enabled);
      setLogoPreview(team.logo_url || null);
      setLogoFile(null);
    }
  }, [team]);

  const dirty =
    name        !== (team?.name || '') ||
    slug        !== (team?.slug || '') ||
    color       !== (team?.primary_color || '#0a0f2e') ||
    language    !== (team?.language_default || 'en') ||
    smsEnabled  !== !!team?.sms_enabled ||
    logoFile    !== null;

  // Handle logo file selection
  const onLogoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setError('Logo must be under 5 MB.');
      return;
    }
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const save = async () => {
    setError(null);
    setSuccess(null);

    if (!name.trim()) { setError('Team name is required.'); return; }
    const finalSlug = (slug || toSlug(name)).trim();
    if (!finalSlug) { setError('Team slug must contain at least one letter or number.'); return; }

    setSaving(true);
    try {
      let logoUrl = team?.logo_url || null;

      // Upload new logo if provided.
      // Stored in `client-docs` bucket under a `__team_logos/` prefix so we don't need
      // a separate bucket. We generate a long-lived signed URL (10 years) so the logo
      // can be displayed publicly in the team's branded portal.
      if (logoFile) {
        const ext = (logoFile.name.split('.').pop() || 'png').toLowerCase();
        const path = `__team_logos/${teamId}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('client-docs')
          .upload(path, logoFile, { upsert: true, contentType: logoFile.type || undefined });
        if (upErr) throw new Error('Logo upload failed: ' + upErr.message);
        // 10-year signed URL — practically permanent
        const { data: signed, error: sErr } = await supabase.storage
          .from('client-docs')
          .createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
        if (sErr) throw new Error('Logo signed URL failed: ' + sErr.message);
        logoUrl = signed?.signedUrl || null;
      } else if (logoPreview === null) {
        // User explicitly removed the logo
        logoUrl = null;
      }

      const { error: updErr } = await supabase
        .from('teams')
        .update({
          name: name.trim(),
          slug: finalSlug,
          primary_color: color,
          language_default: language,
          sms_enabled: smsEnabled,
          logo_url: logoUrl,
        })
        .eq('id', teamId);
      if (updErr) {
        if ((updErr.message || '').toLowerCase().includes('duplicate')) {
          throw new Error('That slug is already in use by another workspace. Try a different one.');
        }
        throw updErr;
      }

      setSuccess('Settings saved.');
      setLogoFile(null);
      await refresh();
    } catch (err) {
      console.error('Save settings failed:', err);
      setError(err.message || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  // Danger zone
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const canDelete = deleteConfirm === team?.name;

  const deleteWorkspace = async () => {
    if (!canDelete || deleting) return;
    setDeleting(true);
    try {
      // The DB schema cascades from teams -> members/roles/templates/uploads/invites
      const { error: delErr } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);
      if (delErr) throw delErr;
      navigate('/hub', { replace: true });
    } catch (err) {
      console.error('Delete workspace failed:', err);
      toast.error('Could not delete: ' + (err.message || 'unknown error'));
      setDeleting(false);
    }
  };

  // ── Render ──
  if (!isOwner) {
    return (
      <>
        <PageHeader
          title="Team settings"
          subtitle="Only workspace owners can edit settings."
        />
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
            background: 'var(--warn-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon.lock size={26} stroke="var(--warn)" />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Owner-only area</h2>
          <p style={{ fontSize: 13.5, color: 'var(--text-3)' }}>
            Ask the workspace owner to update settings, branding, or delete this workspace.
          </p>
        </Card>
      </>
    );
  }

  const initials = (name || '?')
    .split(/\s+/)
    .map(s => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <>
      <PageHeader
        title="Team settings"
        subtitle="Workspace name, branding, language, and team-wide defaults."
        actions={
          <Btn
            variant="primary"
            size="md"
            iconRight={saving ? null : Icon.check}
            onClick={save}
            disabled={!dirty || saving}
          >
            {saving ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Saving…</> : 'Save changes'}
          </Btn>
        }
      />

      {error   && <ErrorBanner message={error} style={{ marginBottom: 14 }} />}
      {success && (
        <div style={{
          padding: '10px 12px', marginBottom: 14,
          background: 'var(--ok-bg)', border: '1px solid rgba(61,199,91,0.22)',
          color: 'var(--ok)', borderRadius: 10, fontSize: 12.5,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Icon.check size={14} />
          {success}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? '1fr'
          : 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: isMobile ? 12 : 16,
      }}>
        {/* Workspace card */}
        <Card style={{ padding: isMobile ? 16 : 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Workspace</div>

          <FieldLabel>Team name</FieldLabel>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            style={{ marginBottom: 12 }}
          />

          <FieldLabel>Public slug</FieldLabel>
          {isMobile ? (
            <>
              <div style={{
                fontSize: 11, color: 'var(--text-3)',
                fontFamily: 'var(--font-mono)', marginBottom: 4,
              }}>
                magicintake.com/
              </div>
              <input
                value={slug}
                onChange={(e) => setSlug(toSlug(e.target.value))}
                disabled={saving}
                className="mi-focusable mi-input"
                style={{
                  boxSizing: 'border-box',
                  width: '100%', height: 44, padding: '0 12px',
                  fontSize: 14, fontFamily: 'inherit', color: 'var(--text)',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)',
                  marginBottom: 4,
                }}
              />
            </>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', marginBottom: 4,
              border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
              background: 'var(--surface)',
              overflow: 'hidden',
            }}>
              <span style={{
                padding: '0 12px', height: 40, display: 'flex', alignItems: 'center',
                fontSize: 13, color: 'var(--text-3)',
                background: 'var(--surface-2)', borderRight: '1px solid var(--border)',
              }}>
                magicintake.com/
              </span>
              <input
                value={slug}
                onChange={(e) => setSlug(toSlug(e.target.value))}
                disabled={saving}
                className="mi-focusable mi-input"
                style={{
                  boxSizing: 'border-box',
                  flex: 1, minWidth: 0, height: 40, padding: '0 12px',
                  fontSize: 13.5, fontFamily: 'inherit', color: 'var(--text)',
                  background: 'transparent', border: 'none', outline: 'none',
                }}
              />
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 14 }}>
            Lowercase letters, numbers, hyphens. Used in your portal URL.
          </div>

          <FieldLabel>Default language</FieldLabel>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={saving}
            className="mi-focusable"
            style={{
              width: '100%', height: 40, padding: '0 12px',
              fontSize: 13.5, fontFamily: 'inherit', color: 'var(--text)',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)', cursor: 'pointer', marginBottom: 14,
            }}
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>

          <FieldLabel>SMS notifications</FieldLabel>
          <button
            type="button"
            onClick={() => setSmsEnabled(!smsEnabled)}
            disabled={saving || (team?.plan || 'free').toLowerCase() !== 'business'}
            style={{
              width: '100%', padding: '12px 14px',
              background: smsEnabled ? 'var(--accent-bg)' : 'var(--surface-2)',
              border: `1.5px solid ${smsEnabled ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 'var(--r-md)',
              cursor: (saving || (team?.plan || 'free').toLowerCase() !== 'business') ? 'not-allowed' : 'pointer',
              textAlign: 'left', fontFamily: 'inherit',
              opacity: (team?.plan || 'free').toLowerCase() !== 'business' ? 0.55 : 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                Send SMS on rejection
                {(team?.plan || 'free').toLowerCase() !== 'business' && (
                  <Badge variant="navy" size="xs" style={{ marginLeft: 6 }}>Business</Badge>
                )}
              </span>
              <span style={{
                width: 30, height: 18, borderRadius: 9, position: 'relative',
                background: smsEnabled ? 'var(--accent)' : 'var(--border-strong)',
                transition: 'background 0.15s',
              }}>
                <span style={{
                  position: 'absolute', top: 2, left: smsEnabled ? 14 : 2,
                  width: 14, height: 14, borderRadius: '50%',
                  background: '#fff', transition: 'left 0.15s',
                }} />
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
              Notify uploaders by SMS (Twilio) when their docs are rejected.
            </div>
          </button>
        </Card>

        {/* Branding card */}
        <Card style={{ padding: isMobile ? 16 : 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>
            Brand the client portal
          </div>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 12 : 16,
            marginBottom: 14,
            alignItems: isMobile ? 'flex-start' : 'center',
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: 14,
              background: logoPreview ? `url(${logoPreview}) center / cover` : color,
              color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, fontWeight: 700,
              flexShrink: 0,
              boxShadow: 'var(--shadow-xs)',
              border: '1px solid var(--border)',
            }}>
              {!logoPreview && initials}
            </div>
            <div>
              <label style={{ display: 'inline-block' }}>
                <input
                  type="file"
                  accept="image/png,image/svg+xml,image/jpeg,image/webp"
                  onChange={onLogoChange}
                  disabled={saving}
                  style={{ display: 'none' }}
                />
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 10px', fontSize: 12, fontWeight: 600,
                  background: 'var(--surface)', color: 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 8,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                  boxShadow: 'var(--shadow-xs)',
                }}>
                  <Icon.upload size={13} />
                  {logoPreview ? 'Replace logo' : 'Upload logo'}
                </span>
              </label>
              {logoPreview && (
                <Btn
                  variant="ghost"
                  size="xs"
                  icon={Icon.trash}
                  onClick={removeLogo}
                  disabled={saving}
                  style={{ marginLeft: 6 }}
                >
                  Remove
                </Btn>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                PNG, SVG, JPG, or WebP. Up to 5 MB.
              </div>
            </div>
          </div>

          <FieldLabel>Primary color</FieldLabel>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {PRESET_COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                disabled={saving}
                title={c}
                style={{
                  width: 32, height: 32, borderRadius: 8, background: c,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  border: color === c ? '2px solid var(--accent)' : '1px solid var(--border)',
                  boxShadow: color === c ? 'var(--shadow-glow)' : 'none',
                  padding: 0,
                  transition: 'all 0.12s',
                }}
              />
            ))}
            <label style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 8,
              background: 'var(--surface-2)', border: '1px dashed var(--border-strong)',
              cursor: 'pointer', position: 'relative',
            }}>
              <Icon.plus size={14} stroke="var(--text-3)" />
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                disabled={saving}
                style={{
                  position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer',
                }}
              />
            </label>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
            Used as your team's accent color in the portal and as the workspace card background on the Hub.
          </div>
        </Card>
      </div>

      {/* Danger zone */}
      <Card style={{
        padding: 20, marginTop: 16,
        borderColor: 'rgba(239,68,68,0.22)',
      }}>
        <div style={{
          fontSize: 14, fontWeight: 700, marginBottom: 6, color: 'var(--danger)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Icon.alert size={14} />
          Danger zone
        </div>

        {!deleteOpen ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Delete this workspace</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                All documents, members, templates, and invites will be permanently removed.
              </div>
            </div>
            <Btn
              variant="danger"
              size="md"
              icon={Icon.trash}
              onClick={() => { setDeleteOpen(true); setDeleteConfirm(''); }}
            >
              Delete workspace
            </Btn>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              This action cannot be undone.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12, lineHeight: 1.5 }}>
              All documents, members, templates, role assignments, invites, and uploaded files will be permanently deleted.
              Type <strong style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{team?.name}</strong> below to confirm.
            </div>
            <Input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder={team?.name || ''}
              disabled={deleting}
              style={{ marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="outline" size="md" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                Cancel
              </Btn>
              <Btn
                variant="danger"
                size="md"
                icon={Icon.trash}
                onClick={deleteWorkspace}
                disabled={!canDelete || deleting}
              >
                {deleting ? <><Spinner size={14} color="rgba(239,68,68,0.5)" /> Deleting…</> : 'Permanently delete'}
              </Btn>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

function FieldLabel({ children }) {
  return (
    <label style={{
      fontSize: 12, fontWeight: 600, color: 'var(--text-2)',
      display: 'block', marginBottom: 6,
    }}>
      {children}
    </label>
  );
}
